import { getCurrent } from '../content/watermark';
import { LearnNotesProps } from '@osmosis/shared/types/learn';
import { NotesPage } from '@osmosis/shared/types/notes';

import { PRINT_NOTES_URL } from '../../constants/urls';

// Print button behavior
const closePrint = (element: HTMLIFrameElement): void => {
  // eslint-disable-next-line no-underscore-dangle
  document.body.removeChild(element);
};

/**
 * Sets up printing block and closes after print.
 */
const setPrint = (element: HTMLIFrameElement, resolve: () => void): void => {
  // Print when container loads
  if (element.contentWindow) {
    element.contentWindow.document.body.style.width = '100%';
    element.contentWindow.document.body.style.height = '100%';
    element.contentWindow.document.body.style.margin = '0';
    element.contentWindow.onbeforeunload = () => closePrint(element);
    element.contentWindow.onafterprint = () => closePrint(element);
    element.contentWindow.focus(); // Required for IE
    element.contentWindow.print();
  }

  resolve();
};

/**
 * Sets iframe styles for printing.
 */
const setIframeStyles = (iFrame: HTMLIFrameElement) => {
  iFrame.style.position = 'fixed';
  iFrame.style.right = '0';
  iFrame.style.bottom = '0';
  iFrame.style.width = '0';
  iFrame.style.height = '0';
  iFrame.style.border = '0';
};

/**
 * Prints composer notes block. `group` narrows the document to one group of the
 * node's notes (see ComposerNoteGroup); without it every note the user may see
 * is printed.
 */
export const print = (
  learnPageId: string | number,
  group?: string,
): Promise<void> => {
  const pageURL = `${PRINT_NOTES_URL}/${learnPageId}${
    group ? `?group=${group}` : ''
  }`;
  return new Promise((resolve) => {
    // Create hidden iFrame
    const hiddenIframe = document.createElement('iframe');
    hiddenIframe.src = pageURL;
    setIframeStyles(hiddenIframe);

    hiddenIframe.onload = () => {
      setPrint(hiddenIframe, resolve);
    };

    document.body.appendChild(hiddenIframe);
  });
};

/**
 * Prints user's notes.
 */
export const printsNotesOfUser = (
  userName: string,
  learnName: string,
  notes: LearnNotesProps[],
): Promise<void> => {
  return new Promise((resolve) => {
    const hiddenIframe = document.createElement('iframe');

    // Create a div to hold all the notes' content
    const notesContainer = document.createElement('div');
    const notesList = document.createElement('div');
    const title = document.createElement('h2');

    title.innerText = `${userName}'s Notes for ${learnName}`;
    notesList.innerHTML = notes
      .map(
        (note) => `
        <p>${note.contents}</p>
      `,
      )
      .join('');

    notesContainer.appendChild(title);
    notesContainer.appendChild(notesList);

    setIframeStyles(hiddenIframe);

    hiddenIframe.onload = () => {
      if (hiddenIframe.contentWindow) {
        hiddenIframe.contentWindow.document.open();
        hiddenIframe.contentWindow.document.write(notesContainer.innerHTML);
        hiddenIframe.contentWindow.document.close();
        return setPrint(hiddenIframe, resolve);
      } else {
        resolve();
      }
    };

    document.body.appendChild(hiddenIframe);
  });
};

/**
 * Prints svg notes.
 */
export const printSvg = (pages: NotesPage[]): Promise<void> => {
  return new Promise((resolve) => {
    let printHtml = '';

    const watermark = getCurrent();

    pages.forEach((page) => {
      printHtml += `<div style="position: relative;"><object data="${page.svg}" height="100%" width="100%"></object><img src="${watermark}" style="position: absolute; width: 25%; bottom: 5%; right: 5%;"></div>`;
    });

    // Create hidden iFrame
    const hiddenIframe = document.createElement('iframe');

    setIframeStyles(hiddenIframe);

    document.body.appendChild(hiddenIframe);

    if (hiddenIframe.contentWindow) {
      hiddenIframe.contentWindow.document.open();
      hiddenIframe.contentWindow.document.write(`<body>${printHtml}</body>`);
      hiddenIframe.contentWindow.document.close();
      hiddenIframe.contentWindow.document.body.onload = () => {
        setPrint(hiddenIframe, resolve);
      };
    } else {
      resolve();
    }
  });
};

const setReceiptStyles = (receiptContainer: HTMLElement) => {
  receiptContainer.style.cssText = `
  font-family: Nunito, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid #ddd;
  padding: 10px;
`;
};

/**
 * Prints receipt.
 */
export const printReceipt = (id: string): Promise<void> => {
  return new Promise((resolve) => {
    const hiddenIframe = document.createElement('iframe');
    const text = document.createElement('h1');
    const receiptContent = document.getElementById(id);
    text.textContent = 'Receipt';

    setIframeStyles(hiddenIframe);

    hiddenIframe.onload = () => {
      if (receiptContent) {
        setReceiptStyles(receiptContent);
        hiddenIframe.contentDocument?.body.appendChild(text);
        hiddenIframe.contentDocument?.body.appendChild(receiptContent);
        return setPrint(hiddenIframe, resolve);
      } else {
        resolve();
      }
    };

    document.body.appendChild(hiddenIframe);
  });
};
